/**
 * A word-level difference between two pieces of prose.
 *
 * Written for the biography assistant, where the whole point of showing a revision
 * is being able to see what changed before accepting it. A character diff would
 * shred words mid-letter and a line diff would mark an entire paragraph as new for
 * one adjusted clause; a word diff matches how someone reads a sentence they wrote.
 *
 * Whitespace travels with the word that precedes it, so joining every segment's
 * text reproduces the input exactly — the rendered diff cannot silently reflow the
 * author's paragraphs.
 */
export type TextDiffSegment = {
  kind: "unchanged" | "added" | "removed";
  text: string;
};

/** Splits into words, each carrying the whitespace that follows it. */
function toWords(value: string): string[] {
  return value.match(/\S+\s*/g) ?? [];
}

/**
 * The longest common subsequence of the two word arrays, as a table of lengths.
 *
 * Quadratic in the number of words, which is the right trade here: a biography is
 * capped at 8,000 characters, so the table stays small, and the alternative
 * heuristics produce diffs that look wrong on exactly the short edits this feature
 * is for.
 */
function lcsLengths(before: string[], after: string[]): number[][] {
  const table: number[][] = Array.from({ length: before.length + 1 }, () =>
    new Array<number>(after.length + 1).fill(0),
  );
  for (let i = before.length - 1; i >= 0; i -= 1) {
    for (let j = after.length - 1; j >= 0; j -= 1) {
      table[i][j] = before[i].trim() === after[j].trim()
        ? table[i + 1][j + 1] + 1
        : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  return table;
}

/** Appends to the last segment when the kind matches, so runs stay whole. */
function push(segments: TextDiffSegment[], kind: TextDiffSegment["kind"], text: string): void {
  const last = segments[segments.length - 1];
  if (last && last.kind === kind) last.text += text;
  else segments.push({ kind, text });
}

export function diffWords(before: string, after: string): TextDiffSegment[] {
  const beforeWords = toWords(before);
  const afterWords = toWords(after);
  const table = lcsLengths(beforeWords, afterWords);

  const segments: TextDiffSegment[] = [];
  let i = 0;
  let j = 0;
  while (i < beforeWords.length && j < afterWords.length) {
    if (beforeWords[i].trim() === afterWords[j].trim()) {
      push(segments, "unchanged", afterWords[j]);
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      push(segments, "removed", beforeWords[i]);
      i += 1;
    } else {
      push(segments, "added", afterWords[j]);
      j += 1;
    }
  }
  while (i < beforeWords.length) {
    push(segments, "removed", beforeWords[i]);
    i += 1;
  }
  while (j < afterWords.length) {
    push(segments, "added", afterWords[j]);
    j += 1;
  }
  return segments;
}

/** Whether anything actually changed, for deciding if there is a revision to offer. */
export function hasChanges(segments: readonly TextDiffSegment[]): boolean {
  return segments.some(segment => segment.kind !== "unchanged");
}

/** The revised text a diff represents, used to confirm nothing is lost on save. */
export function appliedText(segments: readonly TextDiffSegment[]): string {
  return segments.filter(segment => segment.kind !== "removed").map(segment => segment.text).join("");
}
